import { useLanguage } from '../context/LanguageContext';
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Package, ArrowLeft, ChevronDown, ChevronUp } from 'lucide-react';
import { apiClient } from '../context/AuthContext';

const STATUS_LABELS = {
  pending: { label: 'En attente', color: 'bg-yellow-100 text-yellow-700' },
  processing: { label: 'En préparation', color: 'bg-blue-100 text-blue-700' },
  shipped: { label: 'Expédié', color: 'bg-purple-100 text-purple-700' },
  delivered: { label: 'Livré', color: 'bg-green-100 text-green-700' },
  cancelled: { label: 'Annulé', color: 'bg-red-100 text-red-700' },
  refunded: { label: 'Remboursé', color: 'bg-gray-100 text-gray-700' }
};

export default function MyOrders() {
  const { t } = useLanguage();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedOrder, setExpandedOrder] = useState(null);

  useEffect(() => {
    const fetchOrders = async () => {
      try {
        const { data } = await apiClient.get('/orders');
        setOrders(data);
      } catch (err) {
        console.error('Error fetching orders:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchOrders();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#00aaff] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f5f7fa] px-4 md:px-6 py-6 md:py-10" data-testid="my-orders-page">
      <div className="max-w-4xl mx-auto">
        <Link to="/" className="inline-flex items-center gap-2 text-gray-400 hover:text-[#00aaff] text-sm mb-6 transition-colors">
          <ArrowLeft size={16} /> Retour à l'accueil
        </Link>

        <div className="flex items-center gap-3 mb-8">
          <Package size={24} className="text-[#00aaff]" />
          <h1 className="text-3xl font-black text-gray-900">Mes Commandes</h1>
        </div>

        {orders.length === 0 ? (
          <div className="text-center py-24 bg-white rounded-2xl border border-gray-100">
            <Package size={56} className="mx-auto mb-5 text-gray-300" />
            <p className="text-gray-500 mb-6">Vous n'avez pas encore de commandes</p>
            <Link to="/collection" className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm font-bold text-white bg-[#00aaff] hover:bg-[#0088dd]">
              Découvrir la collection
            </Link>
          </div>
        ) : (
          <div className="space-y-4">
            {orders.map(order => {
              const status = STATUS_LABELS[order.status] || STATUS_LABELS.pending;
              const isExpanded = expandedOrder === order.id;
              
              return (
                <div key={order.id} className="bg-white rounded-2xl border border-gray-100 overflow-hidden shadow-sm">
                  <button
                    onClick={() => setExpandedOrder(isExpanded ? null : order.id)}
                    className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-center gap-4">
                      <div>
                        <p className="font-bold text-gray-900 text-left">{order.order_number}</p>
                        <p className="text-xs text-gray-400">
                          {new Date(order.created_at).toLocaleDateString('fr-FR')} à {order.created_time || new Date(order.created_at).toLocaleTimeString('fr-FR', {hour: '2-digit', minute: '2-digit'})}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className={`text-xs font-bold px-3 py-1 rounded-full ${status.color}`}>
                        {status.label}
                      </span>
                      <span className="font-bold text-[#00aaff]">{order.total_ttc?.toFixed(2)}€</span>
                      {isExpanded ? <ChevronUp size={18} className="text-gray-400" /> : <ChevronDown size={18} className="text-gray-400" />}
                    </div>
                  </button>
                  
                  {isExpanded && (
                    <div className="px-6 pb-6 border-t border-gray-100 pt-4">
                      <div className="grid md:grid-cols-2 gap-6">
                        <div>
                          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Articles</p>
                          <div className="space-y-2">
                            {order.items?.map((item, i) => (
                              <div key={i} className="flex items-center gap-3">
                                {item.product_image && (
                                  <img src={item.product_image} alt={item.product_name} className="w-12 h-12 rounded-lg object-cover" />
                                )}
                                <div>
                                  <p className="text-sm font-semibold text-gray-900">{item.product_name}</p>
                                  <p className="text-xs text-gray-400">Qté: {item.quantity} · {item.price}€</p>
                                  {item.color && (
                                    <p className="text-xs text-gray-500">
                                      <span className="font-medium">Couleur:</span> {typeof item.color === 'object' ? item.color.name : item.color}
                                    </p>
                                  )}
                                  {item.lens_option && (
                                    <p className="text-xs text-gray-500">
                                      <span className="font-medium">Verres:</span> {item.lens_option}
                                    </p>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                        <div>
                          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Livraison</p>
                          <p className="text-sm text-gray-600">{order.customer_name}</p>
                          <p className="text-sm text-gray-500">{order.shipping_address}</p>
                          <p className="text-sm text-gray-400 mt-1">{order.shipping_method}</p>
                          {order.tracking_number && (
                            <p className="text-sm text-[#00aaff] mt-2">Suivi: {order.tracking_number}</p>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
